import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useStore } from '../../context/StoreContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CheckInFlow } from '../../components/CheckInFlow';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { FiSearch, FiCheck, FiClock, FiAlertCircle, FiUsers } = FiIcons;

export const CheckIn = () => {
  const navigate = useNavigate();
  const { currentEvent, staffName } = useStore();
  const [query, setQuery] = useState('');
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [successGuest, setSuccessGuest] = useState(null);
  const [guestToUndo, setGuestToUndo] = useState(null);

  useEffect(() => {
    if (currentEvent && currentEvent.event_id) {
      fetchGuests();
      const unsubscribe = api.subscribeToRegistrations(currentEvent.event_id, () => {
        fetchGuests();
      });
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [currentEvent, query]);

  const fetchGuests = async () => {
    if (!currentEvent || !currentEvent.event_id) return;
    setLoading(true);
    const results = await api.searchGuests(currentEvent.event_id, query);
    setGuests(results);
    setLoading(false);
  };

  const handleCheckInComplete = async (answers) => {
    if (!selectedGuest) return;

    let relationshipType = answers.relationship_type;
    if (answers.booked_at_padua) relationshipType = 'Booked Couple';

    await api.checkInGuest(
      selectedGuest.registration_id,
      {
        arrived_guest_count: answers.arrived_guest_count,
        relationship: {
          relationship_type: relationshipType,
          booked_at_padua: answers.booked_at_padua,
          tour_status: answers.tour_status
        }
      },
      { staffName }
    );

    const checkedGuest = { ...selectedGuest, arrived_guest_count: answers.arrived_guest_count };
    setSelectedGuest(null);
    setSuccessGuest(checkedGuest);

    fetchGuests();

    setTimeout(() => {
      setSuccessGuest(null);
      setQuery('');
    }, 2500);
  };

  const handleUndoConfirm = async () => {
    if (!guestToUndo) return;
    await api.undoCheckIn(guestToUndo.registration_id, staffName);
    setGuestToUndo(null);
    fetchGuests();
  };

  if (successGuest) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center animate-in fade-in zoom-in">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-lg">
          <FiCheck className="text-green-600 text-5xl" />
        </div>
        <h1 className="text-4xl font-cinzel text-padua-green mb-2">CHECKED IN</h1>
        <p className="text-2xl font-medium mb-1">{successGuest.contact.first_name} {successGuest.contact.last_name}</p>
        <p className="text-xl text-gray-500 mb-2">{successGuest.rsvp_source} • Party of {successGuest.arrived_guest_count}</p>
        <p className="text-sm font-medium text-gray-400">{format(new Date(), 'h:mm a')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8 hidden md:block">
        <h1 className="text-3xl font-cinzel text-padua-green">THE PADUA MIDSUMMER AFFAIR</h1>
        <p className="text-gray-500 font-medium tracking-wide">AUGUST 23, 2026 • 11:00 AM – 3:00 PM</p>
      </div>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
          <FiSearch className="h-8 w-8 text-padua-sage/60" />
        </div>
        <input
          type="text"
          className="block w-full pl-16 pr-6 py-6 border-2 border-padua-sage/30 rounded-2xl text-2xl placeholder-padua-sage focus:ring-padua-gold focus:border-padua-gold shadow-sm transition-all bg-white"
          placeholder="Search name, email, or phone..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      <div className="space-y-4">
        {loading && guests.length === 0 ? (
          <div className="text-center py-12 text-padua-sage">Loading...</div>
        ) : guests.length === 0 && !query ? (
          <Card className="p-12 text-center flex flex-col items-center justify-center border-dashed">
            <FiUsers className="text-5xl mb-4 text-padua-sage opacity-50" />
            <h3 className="text-2xl font-cinzel text-padua-green mb-2">No guests loaded locally</h3>
            <p className="text-gray-500 mb-8 max-w-md">Use the Admin menu to import a CSV file to this device.</p>
            <Button onClick={() => navigate('/walk-in')} variant="outline" className="px-8">
              Add Walk-In
            </Button>
          </Card>
        ) : guests.length === 0 ? (
          <div className="text-center py-12 text-padua-sage flex flex-col items-center">
            <FiAlertCircle className="text-4xl mb-4 opacity-50" />
            <p className="text-lg">No guests found matching your search.</p>
          </div>
        ) : (
          guests.map(guest => (
            <Card key={guest.registration_id} className="p-0 hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row items-center p-6 gap-6">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-2xl font-cinzel font-bold text-padua-green uppercase">
                    {guest.contact.first_name} {guest.contact.last_name}
                  </h3>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-2 text-sm text-gray-600">
                    <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium">{guest.rsvp_source}</span>
                    {guest.relationship?.relationship_type && (
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${guest.relationship.relationship_type === 'Booked Couple' ? 'bg-padua-gold/20 text-padua-gold' : 'bg-padua-sage/20 text-padua-sage'}`}>
                        {guest.relationship.relationship_type}
                      </span>
                    )}
                    {guest.relationship?.tour_status && (
                      <span className="text-xs text-gray-400">{guest.relationship.tour_status}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">{guest.contact.email} {guest.contact.phone && `• ${guest.contact.phone}`}</p>
                </div>

                <div className="w-full sm:w-auto">
                  {guest.checked_in ? (
                    <div className="bg-gray-50 border border-gray-200 px-6 py-4 rounded-xl text-center w-full sm:w-56 flex flex-col items-center justify-center">
                      <p className="text-sm font-bold text-padua-green uppercase tracking-wider mb-1">ALREADY CHECKED IN</p>
                      <div className="text-xs text-gray-600 mb-2 space-y-1">
                        <p><FiClock className="inline mr-1" /> {guest.check_in_time ? format(new Date(guest.check_in_time), 'h:mm a') : 'Unknown'}</p>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => setGuestToUndo(guest)}
                          className="text-[10px] font-bold text-padua-terracotta hover:text-red-700 transition-colors uppercase tracking-wider border border-padua-terracotta/30 px-2 py-1 rounded w-full"
                        >
                          UNDO CHECK-IN
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSelectedGuest(guest)}
                      className="w-full sm:w-48 bg-padua-green text-white py-4 px-6 rounded-xl text-lg font-medium hover:bg-padua-green/90 transition-colors shadow-sm"
                    >
                      CHECK IN
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {selectedGuest && (
        <CheckInFlow
          guest={selectedGuest}
          onComplete={handleCheckInComplete}
          onCancel={() => setSelectedGuest(null)}
        />
      )}

      {guestToUndo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl p-6 text-center animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-medium text-padua-green mb-4">
              Undo check-in for {guestToUndo.contact.first_name} {guestToUndo.contact.last_name}?
            </h3>
            <p className="text-gray-500 mb-8 text-sm">
              This will mark the guest as not yet arrived. It will not delete their RSVP or profile information.
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="primary" className="w-full bg-red-600 hover:bg-red-700 text-white border-red-600" onClick={handleUndoConfirm}>
                UNDO CHECK-IN
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setGuestToUndo(null)}>
                CANCEL
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};